import { useState } from "react";

export interface UseMissingPaymentMethodBannerParams {
  isEligible: boolean;
  isLoading: boolean;
  isPaymentMethodUndetermined: boolean;
  scopeId: string | undefined;
}

export interface UseMissingPaymentMethodBannerResult {
  showBanner: boolean;
  dismissBanner: () => void;
}

/**
 * Shared visibility and dismissal for the missing-primary-payment-method warning, scoped to
 * whatever identity the caller is showing it for (a client, an encounter, ...). Neither the
 * Services tab nor the encounter Billing tab remounts when that identity changes via in-app
 * navigation, so a plain dismissed boolean leaks across clients/encounters — scoping dismissal
 * to `scopeId` means switching to a different one naturally re-shows the warning there.
 *
 * Every dismissed scope id is tracked, not just the most recent one, so a dismissal sticks even
 * after navigating through other scopes and back.
 */
export function useMissingPaymentMethodBanner(
  params: UseMissingPaymentMethodBannerParams,
): UseMissingPaymentMethodBannerResult {
  const { isEligible, isLoading, isPaymentMethodUndetermined, scopeId } = params;
  const [dismissedScopeIds, setDismissedScopeIds] = useState<ReadonlySet<string>>(() => new Set());

  const showBanner =
    isEligible &&
    !isLoading &&
    isPaymentMethodUndetermined &&
    scopeId !== undefined &&
    !dismissedScopeIds.has(scopeId);

  const dismissBanner = () => {
    if (scopeId === undefined) return;
    setDismissedScopeIds((previousScopeIds) => new Set(previousScopeIds).add(scopeId));
  };

  return { showBanner, dismissBanner };
}
